import { useCallback, useMemo, useState, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'
import { Flex, Text, Skeleton, Checkbox } from '@vtex/shoreline'

import clsx from 'clsx'

import {
  listScreensByTagsKey,
  listScreensByTagsService
} from '../../../../services/place'

import { formatMoney } from '~/helpers'
import { Badge } from '~/components/UI/Badge'
import useQueryParams from '~/hooks/useQuery'
import screenImage from '~/assets/img/elements/screen.png'

import { countSelectedFaces } from '../../helpers'
import { ScreensProps } from '../types'

import s from '../../styles.module.css'
import { useScrollbar } from './useScrollbar'

const FIVE_MINUTES = 5 * 60 * 1000
const TEN_MINUTES = 10 * 60 * 1000

export function useScreens({
  selectedTags,
  selectedScreens,
  setSelectedScreens,
  isEditing,
  allowPlacesSelect,
  isStandard
}: ScreensProps) {
  const { t } = useAPPTranslation()
  const [scrollbarWidth, setScrollbarWidth] = useState(0)
  const listContainerRef = useRef<HTMLDivElement>(null)

  const publisherId = useQueryParams().get('rmid')

  const { data: screens, isLoading } = useQuery({
    enabled: !!selectedTags && selectedTags.length > 0,
    queryKey: [listScreensByTagsKey, publisherId, selectedTags],
    staleTime: FIVE_MINUTES, // 5 minutes - data remains fresh
    gcTime: TEN_MINUTES, // 10 minutes - data remains in cache
    queryFn: async () => {
      if (selectedTags && selectedTags?.length > 0) {
        const payload: RequestListScreensByTagsParams = {
          publisher_id: publisherId!,
          tags: selectedTags,
          count: false
        }

        const response = await listScreensByTagsService(payload)
        const initialSelectedScreens =
          isEditing && allowPlacesSelect && !!selectedScreens
            ? response?.data?.filter(screen =>
                selectedScreens?.some(selected => selected?.id === screen?.id)
              )
            : response?.data

        setSelectedScreens(initialSelectedScreens || [])
        return response?.data
      }
      setSelectedScreens(null)
      return null
    }
  })

  const { getCurrentScrollPosition } = useScrollbar({
    listContainerRef,
    setScrollbarWidth,
    screens,
    selectedScreens
  })

  const handleSelectScreen = useCallback(
    (screen: ListScreensByTagsResponse) => {
      getCurrentScrollPosition()
      let updatedSelectedScreens = [...(selectedScreens || [])]

      const changedScreen = updatedSelectedScreens?.find(
        selected => selected?.id === screen?.id
      )

      if (changedScreen) {
        updatedSelectedScreens = updatedSelectedScreens?.filter(
          selected => selected?.id !== screen?.id
        )
      } else {
        updatedSelectedScreens.push(screen)
      }

      setSelectedScreens(updatedSelectedScreens)
    },
    [selectedScreens]
  )

  const handleSelectAllScreens = useCallback(() => {
    getCurrentScrollPosition()
    if (screens?.length > 0 && selectedScreens?.length === screens?.length) {
      setSelectedScreens([])
    } else {
      setSelectedScreens(screens || [])
    }
  }, [screens, selectedScreens?.length, setSelectedScreens])

  const NoSelectedTags = () => (
    <Flex align="center" justify="center" className={s.modalNoScreensContainer}>
      <Text variant="body" className={clsx(s.textSoft, s.textCenter)}>
        {t('rm:campaign.create.ScreenGroups.Modal.screens.selectSomeTag')}
      </Text>
    </Flex>
  )

  const NoScreens = () => (
    <Flex align="center" justify="center" className={s.modalNoScreensContainer}>
      <Text variant="body" className={clsx(s.textSoft, s.textCenter)}>
        {t(
          'rm:campaign.create.ScreenGroups.Modal.screens.noScreensWithAllTags'
        )}
      </Text>
    </Flex>
  )

  const LoadingScreens = () => {
    const skeletons = Array.from({ length: 6 }, (_, index) => index)

    return (
      <Flex
        direction="column"
        gap="var(--sl-space-2)"
        className={s.modalListContainer}
      >
        {skeletons.map(skeleton => (
          <Skeleton
            key={skeleton}
            width="100%"
            height="100px"
            className={s.skeleton}
          />
        ))}
      </Flex>
    )
  }

  const ScreensList = useCallback(
    () => (
      <>
        {allowPlacesSelect && (
          <Flex
            className={s.selectAllCheckbox}
            style={{
              paddingRight: `calc(var(--sl-space-3) + ${scrollbarWidth}px)`
            }}
          >
            <Text>
              {t(
                'rm:campaign.create.ScreenGroups.Modal.screens.selectAllScreens'
              )}
            </Text>

            <Checkbox
              onChange={handleSelectAllScreens}
              checked={
                screens?.length > 0 &&
                selectedScreens?.length === screens?.length
              }
              indeterminate={
                selectedScreens?.length > 0 &&
                selectedScreens?.length < screens?.length
              }
            />
          </Flex>
        )}
        <Flex
          ref={listContainerRef}
          direction="column"
          gap="var(--sl-space-2)"
          className={s.modalListContainer}
        >
          {screens?.map(screen => {
            const isChecked =
              allowPlacesSelect &&
              !!selectedScreens?.find(selected => selected?.id === screen?.id)

            return (
              <Flex
                key={screen?.id}
                gap="var(--sl-space-3)"
                align="start"
                className={clsx(
                  s.groupContainer,
                  allowPlacesSelect && s.selectableScreen,
                  isChecked && s.checked
                )}
                onClick={() => allowPlacesSelect && handleSelectScreen(screen)}
              >
                <img
                  src={screenImage}
                  alt="screen"
                  width={32}
                  className={s.image}
                />
                <Flex direction="column" gap="var(--sl-space-2)">
                  <Text variant="emphasis">{screen?.name}</Text>
                  {isStandard && (
                    <Text variant="caption2" className={s.textSoft}>
                      CPM: {formatMoney(screen?.price)}
                    </Text>
                  )}
                  <Flex gap="var(--sl-space-0)">
                    <Badge template="primary">
                      {t('rm:campaign.create.ScreenGroups.quantityScreens', {
                        count: screen?.total_faces
                      })}
                    </Badge>
                    {screen?.tags?.map(tag => (
                      <Badge key={tag}>{tag}</Badge>
                    ))}
                  </Flex>
                </Flex>
                {allowPlacesSelect && (
                  <div className={s.marginLeftAuto}>
                    <Checkbox
                      onChange={() => handleSelectScreen(screen)}
                      checked={isChecked}
                    />
                  </div>
                )}
              </Flex>
            )
          })}
        </Flex>
      </>
    ),
    [
      scrollbarWidth,
      allowPlacesSelect,
      screens,
      selectedScreens,
      t,
      handleSelectAllScreens,
      handleSelectScreen,
      isStandard
    ]
  )

  const elements: {
    title: string
    component: React.ReactNode
  } = useMemo(
    () =>
      !selectedTags || selectedTags?.length === 0
        ? {
            title: t(
              'rm:campaign.create.ScreenGroups.Modal.screens.title.noSelectedScreens'
            ),
            component: <NoSelectedTags />
          }
        : isLoading
          ? {
              title: t(
                'rm:campaign.create.ScreenGroups.Modal.screens.title.loadingScreens'
              ),
              component: <LoadingScreens />
            }
          : screens?.length === 0
            ? {
                title: t(
                  'rm:campaign.create.ScreenGroups.Modal.screens.title.noScreens'
                ),
                component: <NoScreens />
              }
            : {
                title: t(
                  `rm:campaign.create.ScreenGroups.${allowPlacesSelect ? 'quantitySelectedScreens' : 'quantityScreens'}`,
                  {
                    count: countSelectedFaces(selectedScreens)
                  }
                ),
                component: <ScreensList />
              },
    [
      selectedTags,
      isLoading,
      screens,
      selectedScreens,
      scrollbarWidth,
      ScreensList,
      t,
      allowPlacesSelect
    ]
  )

  return { elements }
}
